import * as THREE from 'three';
import { disposeObject } from './helpers/ObjectHelpers';
import { buildLine, buildMeasurementArrow } from './MeshHelpers';
import { groupBy, map } from 'lodash';
import { getNumber } from './window/WindowHelpers';

const lineMaterial = new THREE.LineBasicMaterial({
  color: 0x000000,
  opacity: 0.4,
  transparent: true,
  polygonOffset: true,
  polygonOffsetUnits: -5,
  polygonOffsetFactor: -1,
});

const initialMargin = 0.04;
const additionalMargin = 0.02;

var Measurement = function (parent) {
  this.parent = parent;
  this.obj = undefined;
  this.width = undefined;
  this.height = undefined;

  this._buildVerticalArrows = function (container, mullions, fontSize) {
    const groupedMullions = groupBy(mullions, x =>
      getNumber(x.data.def, 'Position')
    );
    const filteredMullions = map(
      groupedMullions,
      x => x.sort((a, b) => a.data.absolutePosY - b.data.absolutePosY)[0]
    );
    filteredMullions.sort((a, b) => a.data.absolutePosY - b.data.absolutePosY);

    const win = parent;
    const flip = parent.parent.imageEngine.flipVerticalMeasurements;

    let xPos = initialMargin;
    for (let i = 0; i < filteredMullions.length; i++) {
      const mullion = filteredMullions[i];
      const position = getNumber(mullion.data.def, 'Position') / 1000;
      const arrowStartY = flip ? -win.height : 0;
      const arrowEndY = -position;
      const arrowData = buildMeasurementArrow(
        -xPos,
        arrowStartY,
        0,
        -xPos,
        arrowEndY,
        0,
        fontSize
      );
      container.add(arrowData.object);

      const lineEndX = mullion.data.absolutePosX - mullion.data.w / 2;
      const line = buildLine(
        -xPos,
        -position,
        0,
        lineEndX,
        -position,
        0,
        lineMaterial
      );
      container.add(line);

      xPos += additionalMargin + arrowData.textMeshHeight;
    }

    const startY = flip ? -win.height : 0;
    const endY = flip ? 0 : -win.height;
    const lastArrowData = buildMeasurementArrow(
      -xPos,
      startY,
      0,
      -xPos,
      endY,
      0,
      fontSize
    );
    container.add(lastArrowData.object);
    xPos += lastArrowData.textMeshHeight;

    this.width = xPos;
  };

  this._buildHorizontalArrows = function (container, mullions, fontSize) {
    const groupedMullions = groupBy(mullions, x =>
      getNumber(x.data.def, 'Position')
    );
    const filteredMullions = map(
      groupedMullions,
      x => x.sort((a, b) => a.data.absolutePosX - b.data.absolutePosX)[0]
    );
    filteredMullions.sort((a, b) => a.data.absolutePosX - b.data.absolutePosX);

    let yPos = initialMargin;
    for (let i = 0; i < filteredMullions.length; i++) {
      const mullion = filteredMullions[i];
      const position = getNumber(mullion.data.def, 'Position') / 1000;
      const arrowData = buildMeasurementArrow(
        0,
        yPos,
        0,
        position,
        yPos,
        0,
        fontSize
      );
      container.add(arrowData.object);

      const lineEndY = mullion.data.h / 2 - mullion.data.absolutePosY;
      const line = buildLine(
        position,
        yPos,
        0,
        position,
        lineEndY,
        0,
        lineMaterial
      );
      container.add(line);

      yPos += additionalMargin + arrowData.textMeshHeight;
    }

    const win = parent;
    const lastArrowData = buildMeasurementArrow(
      0,
      yPos,
      0,
      win.width,
      yPos,
      0,
      fontSize
    );
    container.add(lastArrowData.object);
    yPos += lastArrowData.textMeshHeight;

    this.height = yPos;
  };

  this.build = function () {
    var win = parent;
    var container = new THREE.Object3D();
    container.name = 'Measurement';

    const camDist =
      win.parent.imageEngine && win.parent.imageEngine.camera
        ? win.parent.imageEngine.camera.getCameraDistance()
        : 1.5;
    const rendererHeightModifier =
      win.parent.imageEngine && win.parent.imageEngine.renderer
        ? 800 /
          win.parent.imageEngine.renderer.getSize(new THREE.Vector2()).height
        : 1;
    const fontSize = 20 * rendererHeightModifier * camDist;

    const verticalMullions = win.movableProfiles.filter(
      x => x.data.axis === 'x'
    );
    const horizontalMullions = win.movableProfiles.filter(
      x => x.data.axis === 'y'
    );
    this._buildHorizontalArrows(container, verticalMullions, fontSize);
    this._buildVerticalArrows(container, horizontalMullions, fontSize);

    this.obj = container;
    this.parent.add(this.obj);
  };

  this.clear = function () {
    disposeObject(this.obj);
    this.obj = undefined;
    this.width = undefined;
  };

  this.rebuild = function () {
    if (this.obj) {
      this.parent.remove(this.obj);
      disposeObject(this.obj);
    }
    this.clear();
    this.build();
  };
};

export { Measurement };
